import React from 'react';
import { Option, OptionKey } from './option';
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';
import { CycleHighestMetrics, ScreenCycleWithId } from './types/screen-cycle-table';
import { AppAbility } from '../../ability/defineAbility';
import CycleCell from './CycleCell';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { BG_STATE_COLORS_ZEBRA } from '../CycleTable/constants';
import CycleStatusEnum from '../../enums/cycle-status.enum';
import { ChannelState } from '../../enums/channel-state.enum';
import getTemperatureBorderClass from './utils/getTemperatureBorderClass';
import { setCycleDragPayload } from '../../utils/cycleDragTransfer';
import useEditModeEnabled from '../../hooks/useEditModeEnabled';

export type ScreenCycleTableRowProps = {
  cycleOption: Partial<Option>;
  actionsButtons: IActionsDropdown[];
  cycle: CycleHighestMetrics;
  index: number;
  setSelectedCycle: (value: React.SetStateAction<ScreenCycleWithId>) => void;
  setIsPackageModalOpen: (value: React.SetStateAction<boolean>) => void;
  ability: AppAbility;
  criticalDeviation: number;
  dragContractId?: string;
  dragScreenId?: string | null;
}

const ScreenCycleTableRow: React.FC<ScreenCycleTableRowProps> = ({
  cycleOption,
  actionsButtons,
  cycle,
  index,
  setSelectedCycle,
  setIsPackageModalOpen,
  ability,
  criticalDeviation,
  dragContractId,
  dragScreenId,
}) => {
  const isEditModeEnabled = useEditModeEnabled();
  const canDrag = isEditModeEnabled && !!dragContractId;

  const getBackgroundColor = (): string => {
    const { highest_state } = cycle;
    const { status } = cycle.data;
    const shade = index % 2 === 0 ? 'light' : 'dark';
    const zebraColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-2';

    if (highest_state === ChannelState.STOPPED || status !== CycleStatusEnum.Active) {
      return zebraColor;
    }

    return BG_STATE_COLORS_ZEBRA[highest_state]?.[shade] || zebraColor;
  };

  const pinnedActions = actionsButtons.filter((action) => action.isPinned);

  return (
    <tr
      className={`c-screen-cycle-table-row shadow-inset-table-top text-xs hover:bg-stroke ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${getBackgroundColor()}`}
      draggable={canDrag}
      onDragStart={(event) => {
        if (!canDrag) return;
        setCycleDragPayload(event, cycle.id, dragContractId, dragScreenId ?? null);
      }}
    >
      {!!pinnedActions.length && (
        <td className="c-cycle-table__td py-1">
          <div className="flex flex-row items-center justify-center gap-x-1">
            {pinnedActions.map((action, index) => (
              <InteractiveButton
                key={`pinned-action-${index}`}
                disabled={action.loading || !isEditModeEnabled}
                onClick={() => action.onClick(cycle.id, { ...cycle.data })}
                customType="min-h-fit min-w-fit max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer"
                style={{ padding: '4px' }}
                as={action?.tag || IActionsTag.Button}
              >
                {action.icon(cycle.data)}
              </InteractiveButton>
            ))}
          </div>
        </td>
      )}
      {Object.entries(cycleOption).map(([optionKey, option]) => {
        const borderClass = getTemperatureBorderClass(optionKey, cycle, option.BLOCK_PARAMETERS, criticalDeviation);

        const enhancedOption = {
          ...option,
          BLOCK_PARAMETERS: `${option.BLOCK_PARAMETERS} ${borderClass}`,
        };

        return (
          <CycleCell
            key={optionKey}
            optionKey={optionKey as OptionKey}
            option={enhancedOption}
            cycle={cycle}
            actionsButtons={actionsButtons}
            setSelectedCycle={setSelectedCycle}
            setIsPackageModalOpen={setIsPackageModalOpen}
            ability={ability}
          />
        )
      })}
    </tr>
  );
};

export default ScreenCycleTableRow;
